import { useCallback, useMemo } from 'react'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import { useTaskLabels } from '@/lib/db/rxdb/hooks'
import { useFilteredTasks } from '@/routes/features/tasks/hooks/useFilteredTasks'

/**
 * Returns top-level open tasks attached to the given label.
 *
 * Mirrors the inbox semantics: non-archived, non-subtask, with an open status.
 * If `labelId` is undefined the hook returns an empty list and stays idle.
 */
export function useTasksByLabel(
  labelId: string | undefined,
  options?: { includeSubTasks?: boolean },
) {
  const includeSubTasks = options?.includeSubTasks ?? false
  const { taskLabels, isLoading: taskLabelsLoading } = useTaskLabels(
    labelId ? { labelId, isDeleted: false } : undefined,
  )

  const taskIdSet = useMemo(
    () => new Set(taskLabels.map((tl) => tl.taskId)),
    [taskLabels],
  )

  const predicate = useCallback(
    (task: TaskDocument, openStatusIds: ReadonlyArray<string>) =>
      !task.isArchived &&
      (includeSubTasks || !task.parentId) &&
      openStatusIds.includes(task.statusId) &&
      taskIdSet.has(task.id),
    [taskIdSet, includeSubTasks],
  )

  const { tasks, isLoading: filteredLoading } = useFilteredTasks({ predicate })

  return {
    tasks: labelId ? tasks : [],
    isLoading: !!labelId && (filteredLoading || taskLabelsLoading),
  }
}
